import simpleGit, { type SimpleGit, type LogResult } from "simple-git";
import { execFile } from "node:child_process";
import { promisify } from "node:util";

const execFileAsync = promisify(execFile);

// Don't cache — SimpleGit instances are lightweight and caching causes cross-cwd bugs
export function getGit(cwd?: string): SimpleGit {
  return simpleGit(cwd ?? process.cwd());
}

/** Returns true if the error indicates the file has no git history (untracked or empty log). */
function isNoHistoryError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const msg = String((err as { message?: string }).message ?? "").toLowerCase();
  return (
    msg.includes("does not have any commits yet") ||
    msg.includes("hat noch keine commits") ||       // German git
    msg.includes("no tiene ningún commit") ||        // Spanish git
    msg.includes("unknown revision") ||
    msg.includes("bad default revision") ||
    msg.includes("fatal") && msg.includes("commit")  // catch-all for translated "no commits" messages
  );
}

/** Get days since a file was last modified in git.
 *  Returns null for untracked files. Throws on git failures. */
export async function daysSinceLastChange(
  filePath: string,
  cwd?: string
): Promise<number | null> {
  try {
    const git = getGit(cwd);
    const log = await git.log({ file: filePath, maxCount: 1 });
    if (!log.latest?.date) return null;
    const lastDate = new Date(log.latest.date);
    const now = new Date();
    return Math.floor(
      (now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24)
    );
  } catch (err) {
    if (isNoHistoryError(err)) return null;
    throw err;
  }
}

/** Get number of commits since a file was last modified.
 *  Uses git rev-list --count to avoid fetching all commit objects.
 *  Returns null for untracked files. Throws on git failures. */
export async function commitsSinceLastChange(
  filePath: string,
  cwd?: string
): Promise<number | null> {
  const MAX_LOOK_BACK = 200;
  try {
    const git = getGit(cwd);
    const fileLog = await git.log({ file: filePath, maxCount: 1 });
    if (!fileLog.latest?.hash) return null;

    const raw = await git.raw(["rev-list", "--count", `${fileLog.latest.hash}..HEAD`]);
    const count = parseInt(raw.trim(), 10);
    if (isNaN(count)) return null;
    return Math.min(count, MAX_LOOK_BACK);
  } catch (err) {
    if (isNoHistoryError(err)) return null;
    throw err;
  }
}

/** Get both days and commits since a file was last modified — one git call instead of two.
 *  Returns nulls for untracked files. Throws on git failures. */
export async function getFileGitInfo(
  filePath: string,
  cwd?: string
): Promise<{ days: number | null; commits: number | null }> {
  const MAX_LOOK_BACK = 200;
  try {
    const git = getGit(cwd);
    const log = await git.log({ file: filePath, maxCount: 1 });
    if (!log.latest?.date || !log.latest?.hash) return { days: null, commits: null };

    const lastDate = new Date(log.latest.date);
    const now = new Date();
    const days = Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));

    const raw = await git.raw(["rev-list", "--count", `${log.latest.hash}..HEAD`]);
    const count = parseInt(raw.trim(), 10);
    const commits = isNaN(count) ? null : Math.min(count, MAX_LOOK_BACK);

    return { days, commits };
  } catch (err) {
    if (isNoHistoryError(err)) return { days: null, commits: null };
    throw err;
  }
}

export async function getGitDiff(
  paths: string[],
  cwd?: string
): Promise<string> {
  try {
    const git = getGit(cwd);
    return await git.diff(["HEAD~2", "HEAD", "--", ...paths]);
  } catch {
    return "";
  }
}

/**
 * Batch git info for multiple files in 1-2 git calls total (down from 2N).
 *
 * Call 1: Single `git log --name-only` covering all files, parsed to extract
 *         the most recent commit hash + date per file.
 * Call 2: One `git rev-list --count` per unique hash (deduplicated — files that
 *         share their last commit share the count query).
 */
export async function batchFileGitInfo(
  filePaths: string[],
  cwd: string
): Promise<Map<string, { days: number | null; commits: number | null }>> {
  const result = new Map<string, { days: number | null; commits: number | null }>();
  if (filePaths.length === 0) return result;

  const now = Date.now();

  // Step 1: single git log call → parse per-file latest commit
  const fileLastCommit = new Map<string, { hash: string; date: Date }>();
  try {
    // Limit to last 200 commits — beyond that, files are certainly "stale"
    const { stdout } = await execFileAsync(
      "git",
      ["log", "--max-count=200", "--format=%H %aI", "--name-only", "--diff-filter=ACMR", "--", ...filePaths],
      { cwd, maxBuffer: 10 * 1024 * 1024 }
    );

    // Parse output: alternating "hash date" lines and filename lines
    let currentHash = "";
    let currentDate = "";
    for (const line of stdout.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      // Hash + date line: 40-char hex + space + ISO date
      if (/^[0-9a-f]{40} /.test(trimmed)) {
        const spaceIdx = trimmed.indexOf(" ");
        currentHash = trimmed.slice(0, spaceIdx);
        currentDate = trimmed.slice(spaceIdx + 1);
        continue;
      }

      // Filename line — only record first (= most recent) occurrence per file
      if (currentHash && !fileLastCommit.has(trimmed)) {
        fileLastCommit.set(trimmed, { hash: currentHash, date: new Date(currentDate) });
      }
    }
  } catch {
    // Git failed entirely — return nulls for everything
    for (const fp of filePaths) result.set(fp, { days: null, commits: null });
    return result;
  }

  // Step 2: batch rev-list — one call per unique hash
  const uniqueHashes = [...new Set([...fileLastCommit.values()].map((v) => v.hash))];
  const hashCommitCount = new Map<string, number>();

  await Promise.all(uniqueHashes.map(async (hash) => {
    try {
      const { stdout } = await execFileAsync("git", ["rev-list", "--count", `${hash}..HEAD`], { cwd });
      const count = parseInt(stdout.trim(), 10);
      if (!isNaN(count)) hashCommitCount.set(hash, count);
    } catch {
      // skip
    }
  }));

  // Step 3: assemble results
  for (const fp of filePaths) {
    const info = fileLastCommit.get(fp);
    if (!info) { result.set(fp, { days: null, commits: null }); continue; }
    const days = Math.floor((now - info.date.getTime()) / (1000 * 60 * 60 * 24));
    const commits = hashCommitCount.get(info.hash) ?? null;
    result.set(fp, { days, commits });
  }

  return result;
}

/**
 * Count how many recent commits touched each file — a churn signal.
 *
 * One `git log --name-only` call over the last `maxCommits` commits, tallying
 * an occurrence per file per commit. Keys are paths relative to the repo root,
 * exactly as git reports them. Returns an empty map if git is unavailable.
 */
export async function churnByFile(
  cwd: string,
  maxCommits = 200
): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  try {
    const { stdout } = await execFileAsync(
      "git",
      ["log", `--max-count=${maxCommits}`, "--format=%H", "--name-only", "--diff-filter=ACMR"],
      { cwd, maxBuffer: 10 * 1024 * 1024 }
    );

    for (const line of stdout.split("\n")) {
      const trimmed = line.trim();
      // Skip blanks and the 40-char commit-hash separator lines.
      if (!trimmed || /^[0-9a-f]{40}$/.test(trimmed)) continue;
      counts.set(trimmed, (counts.get(trimmed) ?? 0) + 1);
    }
  } catch {
    return counts;
  }
  return counts;
}

/** Get the current HEAD commit hash, or null when the repo has no commits yet. Never throws. */
export async function getHead(cwd?: string): Promise<string | null> {
  try {
    const git = getGit(cwd);
    const raw = await git.raw(["rev-parse", "HEAD"]);
    const head = raw.trim();
    return head || null;
  } catch {
    return null;
  }
}

/** Get full git log */
export async function getLog(
  cwd?: string,
  maxCount = 50
): Promise<LogResult> {
  const git = getGit(cwd);
  return git.log({ maxCount });
}

/** Get list of files changed since the last commit (staged + unstaged) */
export async function getChangedFiles(cwd?: string): Promise<string[]> {
  try {
    const git = getGit(cwd);
    const status = await git.status();
    return [
      ...status.modified,
      ...status.created,
      ...status.renamed.map((r: { to: string }) => r.to),
      ...status.deleted,
      ...status.staged,
    ].filter((v, i, a) => a.indexOf(v) === i); // deduplicate
  } catch {
    return [];
  }
}
